import { isAdminOrSuperAdmin } from './userTypes';
import { hasAnyAccessPermission } from './utils';
import HISTORY_ROUTE from '../routes/admin/historyRoute';
import {
  ADMIN_DASHBOARD,
  ADMIN_PENDING_HIRES,
  ADMIN_TIME_OFF,
  ADMIN_TIME_OFF_PAYWALL,
  PERMISSION_VIEW_AND_EDIT,
} from './constants';
import {
  FEATURE_FLAG_ABSENCE_MANAGEMENT,
  FEATURE_FLAG_ABSENCE_MANAGEMENT_ADMIN,
  FEATURE_FLAG_WORKING_PATTERNS,
  FEATURE_FLAG_WORKING_PATTERNS_ADMIN,
} from '@ApplicationWide/feature-flags';

export const ADMIN_VISIBILITY = [
  'dashboard',
  'reports',
  'tasks',
  'documents',
  'general',
  'records',
  'groups',
  'emails',
  'integrations',
  'permissions',
  'timeOff',
];

const COMPANY_CHECK_FEATURE_FLAG_EXCEPTIONS = [
  FEATURE_FLAG_ABSENCE_MANAGEMENT,
  FEATURE_FLAG_ABSENCE_MANAGEMENT_ADMIN,
  FEATURE_FLAG_WORKING_PATTERNS,
  FEATURE_FLAG_WORKING_PATTERNS_ADMIN,
];

const isNoCompanyOrTimeOffEnabled = (company) => !company || company?.baseDetails?.enabledTimeOff;

const hasTimeOffPaywallEnabled = (company) => company?.baseDetails.ptoPaywallFeatureFlag;

const isTimeOffEnabled = (company) => hasTimeOffPaywallEnabled(company) && !isNoCompanyOrTimeOffEnabled(company);

const isCompanyPlanOnboarding = (company) => company?.baseDetails.companyPlan === 'onboarding';

export const hasAdminVisibility = (user) => Boolean(user.userRole?.permissions?.adminVisibility);

export const canViewAdminArea = (area, user, company = null, skipCompanyCheck = false) =>
  (skipCompanyCheck || area !== ADMIN_TIME_OFF || isNoCompanyOrTimeOffEnabled(company)) &&
  isAdminOrSuperAdmin(user) &&
  hasAdminVisibility(user) &&
  hasAnyAccessPermission(user.userRole?.permissions?.adminVisibility[area]);

export const canEditAdminArea = (area, user) =>
  isAdminOrSuperAdmin(user) &&
  hasAdminVisibility(user) &&
  user.userRole?.permissions?.adminVisibility[area] === PERMISSION_VIEW_AND_EDIT;

export const canViewAnyAdminOptions = (user) => {
  const permissions = user.userRole?.permissions?.adminVisibility || {};
  return Object.values(permissions).some((perm) => hasAnyAccessPermission(perm));
};

export const canViewAdminRoute = (route, loggedInUser, company) => {
  if (route.area === ADMIN_PENDING_HIRES) {
    return canEditAdminArea(ADMIN_DASHBOARD, loggedInUser);
  }
  if (route.area === ADMIN_TIME_OFF_PAYWALL) {
    const isAbsenceManagementEnabled =
      company?.baseDetails[FEATURE_FLAG_ABSENCE_MANAGEMENT] ||
      company?.baseDetails[FEATURE_FLAG_ABSENCE_MANAGEMENT_ADMIN];
    return isTimeOffEnabled(company) && canViewAdminArea(ADMIN_TIME_OFF, loggedInUser) && !isAbsenceManagementEnabled;
  }
  if (route.route === HISTORY_ROUTE.route) {
    return !isCompanyPlanOnboarding(company);
  }

  const routeFeatureFlagArray = Array.isArray(route.featureFlag) ? route.featureFlag : [route.featureFlag];
  const skipCompanyCheck = routeFeatureFlagArray.some((flag) => COMPANY_CHECK_FEATURE_FLAG_EXCEPTIONS.includes(flag));
  return canViewAdminArea(route.adminVisibilityPermission, loggedInUser, company, skipCompanyCheck);
};
